import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, IconButton, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import Typography from "components/basic/typography";

import ConversationThread from "../ConversationThread";
import { ConversationView } from "../../types";

interface ChatWindowProps {
  conversation: ConversationView;
  onBack?: () => void;
  // Moves the chat into a floating popup so it can continue anywhere.
  onPopOut?: () => void;
  onToggleStar: () => void;
  onToggleArchive: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  conversation,
  onBack,
  onPopOut,
  onToggleStar,
  onToggleArchive,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("messages");

  return (
    <Box display="flex" flexDirection="column" height="100%" minWidth={0}>
      <Box
        display="flex"
        alignItems="center"
        gap={theme.spacing(1)}
        px={2}
        py={1}
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        {onBack && (
          <IconButton onClick={onBack} aria-label={t("conversation.back")}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar
          src={conversation.otherPartyAvatarUrl ?? undefined}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{ width: 44, height: 44, flexShrink: 0 }}
        />
        {/* Without the truncation a long name pushes the three actions off a
            phone-width row. */}
        <Typography
          variant="h4"
          noWrap
          style={{ fontWeight: "bold", flexGrow: 1 }}
          sx={{ minWidth: 0 }}
        >
          {conversation.otherPartyName}
        </Typography>
        {onPopOut && (
          <IconButton
            onClick={onPopOut}
            aria-label={t("conversation.pop_out")}
            title={t("conversation.pop_out")}
          >
            <PictureInPictureAltIcon />
          </IconButton>
        )}
        <IconButton
          onClick={onToggleStar}
          aria-label={t(
            conversation.starred ? "conversation.unstar" : "conversation.star",
          )}
        >
          {conversation.starred ? (
            <StarIcon sx={{ color: theme.palette.warning.main }} />
          ) : (
            <StarBorderIcon />
          )}
        </IconButton>
        <IconButton
          onClick={onToggleArchive}
          aria-label={t(
            conversation.archived
              ? "conversation.unarchive"
              : "conversation.archive",
          )}
        >
          {conversation.archived ? (
            <UnarchiveOutlinedIcon />
          ) : (
            <ArchiveOutlinedIcon />
          )}
        </IconButton>
      </Box>

      <ConversationThread key={conversation.id} conversation={conversation} />
    </Box>
  );
};

export default ChatWindow;
